/**
 * 客戶 / 廠商挑選器(REQ-20260806002)。
 *
 * 需求:「輸入客戶/廠商編號後,必須自動帶入現有的客戶主檔資料
 * (如統編、送貨地址、電話)。」
 *
 * 打編號 → 下拉候選 → 選定後把整包主檔資料交給父層(父層負責填進表單欄位),
 * 並就地把統編 / 地址 / 電話顯示出來讓會計核對,不用再開一個視窗查客戶。
 * 顯示順序與「收款憑證」標題見 REQ-20260831006。
 * 客戶端地址標題見 REQ-20260831007。
 * 廠商端地址標題見 REQ-20260902008。
 * 廠商主檔「稅額」那一格(REQ-20260902007 加、REQ-20260902012 從進貨單移除)已於
 * REQ-20260903001 從本元件整個刪除,所有呼叫點(含應付對帳)都不再顯示。
 *
 * 例外:進貨單的「代管客戶」不是付款對象,核對用的統編 / 地址那些欄位在那裡是雜訊,
 * 所以開了 `visibleFields` 讓它只留電話(REQ-20260819001)。不傳就是全部顯示。
 *
 * 候選清單支援 ↑ / ↓ + Enter 選定(REQ-20260903010)。
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { PartyInfo } from '@app/shared';
import { usePartyLookup } from '../features/accounting/api';
import { useListKeyboardNav } from './useListKeyboardNav';

type PartyField = 'taxId' | 'address' | 'phone' | 'settlementCycle' | 'paymentCert';

const ALL_FIELDS: PartyField[] = ['phone', 'address', 'taxId', 'settlementCycle', 'paymentCert'];

interface Props {
  type: 'customer' | 'supplier';
  /** 已選定的對象;null 表示還沒選 */
  value: PartyInfo | null;
  onChange: (party: PartyInfo | null) => void;
  label?: string;
  /**
   * 覆寫欄位標題文字;不傳 = `title` + 「編號 / 名稱」。
   * placeholder 與「找不到符合的…」訊息仍然用 `title`(REQ-20260903003)。
   *
   * 型別自 REQ-20260905006 起放寬成 `ReactNode`,好讓呼叫端在標題右邊接一個
   * 「選填」之類的灰色小標。**傳字串時 DOM 與 class 逐字不變**,既有呼叫點不受影響。
   */
  labelText?: ReactNode;
  disabled?: boolean;
  /** 選定後要顯示哪幾格主檔資料;不傳 = 全部(候選清單不受影響) */
  visibleFields?: PartyField[];
  /**
   * 輸入框的 `id`(以及 label 的 `htmlFor`)。**不傳時是既有的 `party-{type}`,
   * DOM 逐字不變**(REQ-20260907002)。
   *
   * 為什麼需要:運費明細**每一列都有一格「送達客戶」**,同一個畫面上會出現好幾顆
   * customer 挑選器 —— 沿用寫死的 id 會讓多個 `<input>` 共用同一個 id,
   * label 全部指向第一顆,`getByLabelText()` 也會一次抓到好幾個。
   * 只有需要多顆的呼叫點才傳,既有呼叫點一個字都不用改。
   */
  inputId?: string;
}

export function PartyPicker({
  type,
  value,
  onChange,
  label,
  labelText,
  disabled,
  visibleFields = ALL_FIELDS,
  inputId,
}: Props): JSX.Element {
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, error } = usePartyLookup(type, keyword);

  // 選定之後就不要再開著候選清單擋畫面
  useEffect(() => {
    if (value) setOpen(false);
  }, [value]);

  const title = label ?? (type === 'customer' ? '客戶' : '廠商');
  const fieldId = inputId ?? `party-${type}`;
  const items = data?.items ?? [];

  // 候選清單沒開著就不接管方向鍵 —— 那時鍵盤該做的是在輸入框裡移動游標
  const listOpen = open && keyword.trim() !== '';
  const itemRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const nav = useListKeyboardNav(listOpen ? items.length : 0, (i) => {
    const p = items[i];
    if (p) onChange(p);
  });

  // 高亮跑出可視範圍時把該筆捲進來(jsdom 沒有 scrollIntoView,要 optional chaining)
  const activeId = nav.index >= 0 ? items[nav.index]?.id : undefined;
  useEffect(() => {
    if (activeId != null) itemRefs.current[activeId]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  return (
    <div className="grid gap-1">
      <label className="text-sm text-slate-600" htmlFor={fieldId}>
        {labelText ?? `${title}編號 / 名稱`}
      </label>

      {value ? (
        <div className="flex items-start justify-between gap-2 border rounded-md bg-white px-3 py-2">
          <div className="text-sm">
            <div className="font-medium text-slate-900">
              {value.simpleName}
              {value.code ? (
                <span className="ml-1 text-slate-400 font-mono text-xs">{value.code}</span>
              ) : null}
            </div>
            <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs text-slate-500">
              {visibleFields.includes('phone') ? (
                <>
                  <dt>電話</dt>
                  <dd>{value.phone ?? '—'}</dd>
                </>
              ) : null}
              {visibleFields.includes('address') ? (
                <>
                  <dt>地址</dt>
                  <dd>{value.address ?? '—'}</dd>
                </>
              ) : null}
              {visibleFields.includes('taxId') ? (
                <>
                  <dt>統編</dt>
                  <dd>{value.taxId ?? '—'}</dd>
                </>
              ) : null}
              {visibleFields.includes('settlementCycle') && value.settlementCycleLabel ? (
                <>
                  <dt>結帳週期</dt>
                  <dd>
                    {value.settlementCycleLabel}
                    {value.settlementDay ? ` / ${value.settlementDay} 日結` : ''}
                  </dd>
                </>
              ) : null}
              {visibleFields.includes('paymentCert') && value.paymentCert ? (
                <>
                  <dt>收款憑證</dt>
                  <dd>{value.paymentCert}</dd>
                </>
              ) : null}
            </dl>
          </div>
          {disabled ? null : (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setKeyword('');
                setOpen(true);
              }}
              className="text-blue-600 hover:underline text-xs whitespace-nowrap"
            >
              重新選擇
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            id={fieldId}
            type="text"
            value={keyword}
            disabled={disabled}
            onChange={(e) => {
              setKeyword(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (listOpen) nav.onKeyDown(e);
            }}
            placeholder={`輸入${title}編號或名稱`}
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          />

          {listOpen ? (
            <div className="absolute z-10 mt-1 w-full border rounded-md bg-white shadow-sm max-h-64 overflow-auto">
              {isLoading ? (
                <div role="status" className="px-3 py-2 text-sm text-slate-500">
                  查詢中...
                </div>
              ) : isError ? (
                <div role="alert" className="px-3 py-2 text-sm text-red-600">
                  查詢失敗：{error instanceof Error ? error.message : '未知錯誤'}
                </div>
              ) : items.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-500">
                  找不到符合的{title}。請確認編號,或先到{title}主檔建檔。
                </div>
              ) : (
                <ul>
                  {items.map((p, i) => (
                    <li key={p.id}>
                      <button
                        ref={(el) => {
                          itemRefs.current[p.id] = el;
                        }}
                        type="button"
                        onClick={() => onChange(p)}
                        // hover 變體會蓋掉高亮底色,所以兩者擇一輸出而不是並存
                        className={`w-full text-left px-3 py-2 text-sm ${
                          i === nav.index ? 'bg-blue-100' : 'hover:bg-slate-50'
                        }`}
                        aria-selected={i === nav.index ? true : undefined}
                        data-testid={i === nav.index ? 'kbd-active' : undefined}
                      >
                        <span className="font-medium">{p.simpleName}</span>
                        {p.code ? (
                          <span className="ml-1 text-slate-400 font-mono text-xs">{p.code}</span>
                        ) : null}
                        {p.taxId ? (
                          <span className="ml-2 text-slate-400 text-xs">統編 {p.taxId}</span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
